<template>
  <div v-if="table_permissions.length > 0" class="v-datatable-shell h-100">
    <!-- slot top -->
    <div v-if="$slots.top">
      <slot name="top"></slot>
    </div>
    <!-- slot top end -->
    <div class="v-datatable-scroll">
      <v-data-table
        v-model="selected"
        fixed-header
        light
        dense
        height="100%"
        :show-select="!disableSelectAllCheckbox"
        :single-select="disableSelectAllCheckbox"
        :items="items"
        :headers="headers"
        :sort-by.sync="orderBy"
        :sort-desc.sync="sortOrder"
        :items-per-page="itemPerPage"
        :item-key="id_field_name"
        class="mx-2 mt-0 font-weight-bold"
        hide-default-footer
        mobile-breakpoint="40"
        @dblclick:row="handleDoubleClick"
      >
        <!--    no-data-->
        <template v-slot:no-data> {{ $t("datatable_no_data_here") }}</template>
        <template v-slot:no-results>
          {{ $t("datatable_no_results_here") }}</template
        >
        <!--    no-data end-->
        <!--    table top-->
        <template v-slot:top>
          <div
            class="ma-0 mb-10 pa-0 pt-2 d-flex justify-space-between align-center flex-wrap bg-white position-sticky top-0 zindex-5"
            :class="{ 'flex-column': $vuetify.breakpoint.xsOnly }"
          >
            <datatable-detail
              :table_properties="table_properties"
              v-if="$vuetify.breakpoint.smAndUp"
            ></datatable-detail>
            <div
              v-else
              class="d-flex align-center justify-space-between mb-3 w-100"
            >
              <datatable-detail
                :table_properties="table_properties"
              ></datatable-detail>
              <b-button
                v-show="$vuetify.breakpoint.xsOnly"
                depressed
                small
                variant="outline-primary"
                class="px-5 py-3 mb-1 btn--tools"
                @click="toolBarVisionState = !toolBarVisionState"
              >
                {{ $t("tools") }}
              </b-button>
            </div>
            <div
              v-show="isToolbarVisiable"
              style="
                display: flex;
                justify-content: center;
                align-items: center;
              "
              class="card-toolbar ma-0 pa-0 col-md-8 col-sm-10 col-12 row justify-content-start justify-content-sm-end justify-content-md-end align-center align-content-center"
            >
              <div
                class="me-md-2 me-sm-1 mb-1 me-0 pa-0"
                :class="{ 'col-6 px-8 mt-3': $vuetify.breakpoint.xsOnly }"
                v-if="bulkRemoveOption && selected.length > 0"
              >
                <button
                  @click.prevent="resetSelectedItems"
                  class="btn btn--export-filter"
                >
                  <span class="svg-icon">
                    <v-icon size="18">mdi-refresh</v-icon>
                  </span>
                  {{ $t("reset") }}
                </button>
              </div>
              <div
                class="me-md-2 me-sm-1 mb-1 me-0 pa-0"
                :class="{ 'col-6 px-8 mt-3': $vuetify.breakpoint.xsOnly }"
                v-if="permissionChecker('create') && addComponent"
              >
                <button
                  @click.prevent="showAddItemModal"
                  class="btn btn--export-filter"
                >
                  <span class="svg-icon">
                    <v-icon size="18">mdi-plus-box-outline</v-icon>
                  </span>
                  <span v-if="addButtonText"
                    >{{ $t("add") }}
                    {{ translateApiLabel(addButtonText) }}</span
                  >
                  <span v-else
                    >{{ $t("add") }}
                    {{ translateApiLabel(table_properties.title) }}</span
                  >
                </button>
              </div>

              <!--begin::Button-->

              <div
                class="me-md-2 me-sm-1 mb-1 me-0 pa-0"
                :class="{ 'col-6 px-8 mt-3': $vuetify.breakpoint.xsOnly }"
                v-if="permissionChecker('export') && exportHeaders"
              >
                <button
                  @click="handleExportSidebarClick"
                  class="btn btn--export-filter"
                >
                  <span class="svg-icon">
                    <v-icon size="18">mdi-export-variant</v-icon>
                  </span>
                  {{ $t("export") }}
                </button>
              </div>

              <!--end::Button-->

              <div
                class="me-md-2 me-sm-1 mb-1 me-0 pa-0"
                :class="{ 'col-6 px-8 mt-3': $vuetify.breakpoint.xsOnly }"
                v-if="filters?.length > 0"
              >
                <button
                  @click="handleFilterSidebarClick"
                  class="btn btn--export-filter"
                >
                  <span class="svg-icon">
                    <v-icon size="18">mdi-filter</v-icon>
                  </span>
                  {{ $t("filter") }}
                </button>
              </div>

              <div
                class="me-md-2 me-sm-1 mb-1 me-0 pa-0"
                :class="{ 'col-6 mx-8': $vuetify.breakpoint.xsOnly }"
                v-if="permissionChecker('print')"
              >
                <button
                  class="btn btn--export-filter"
                  @click.prevent="showGenerateModal"
                >
                  {{ $t("generate") }}
                </button>
                <component
                  v-if="permissionChecker(generatePermission)"
                  v-bind:is="generateComponent"
                  ref="generate"
                  :updateTableContent="updateTableContent"
                  :pageLoader="pageLoad"
                ></component>
              </div>

              <div
                class="me-md-2 me-sm-1 mb-1 me-0 pa-0"
                :class="{ 'col-6 mx-8': $vuetify.breakpoint.xsOnly }"
                v-if="permissionChecker('reprocess')"
              >
                <button
                  class="btn btn--export-filter"
                  @click.prevent="applyReprocessAction"
                >
                  {{ $t("reprocess") }}
                </button>
              </div>
              <component
                v-bind:is="printComponent"
                v-if="permissionChecker('sku_barcode_generate')"
                :items="selected"
                :types="[
                  { name: $t('barcode'), value: 'barcode2d' },
                  { name: $t('qr'), value: 'barcodeQr' },
                ]"
                :class="{ 'col-6': $vuetify.breakpoint.xsOnly }"
              ></component>

              <div
                class="me-md-2 me-sm-1 mb-1 me-0 pa-0"
                :class="{ 'col-6 mx-8': $vuetify.breakpoint.xsOnly }"
                v-else-if="
                  permissionChecker('upload') &&
                  table_properties.title !== 'SKUs'
                "
              >
                <button
                  class="btn btn--export-filter"
                  @click.prevent="
                    showUploadModal($store.getters[store_names.getUploadType])
                  "
                >
                  <span class="svg-icon">
                    <v-icon size="18">mdi-cloud-upload</v-icon>
                  </span>
                  {{ $t("import") }}
                </button>
                <component
                  v-if="permissionChecker('upload')"
                  v-bind:is="uploadComponent"
                  ref="upload"
                  :updateTableContent="updateTableContent"
                ></component>
              </div>

              <component
                v-bind:is="multiUploadComponent"
                v-if="
                  (permissionChecker('upload') &&
                    table_properties.title === 'SKUs') ||
                  table_properties.title === 'Products'
                "
                :types="$store.getters[store_names.getUploadType]"
                :actionFunction="showUploadModal"
                :class="{ 'col-6': $vuetify.breakpoint.xsOnly }"
              ></component>

              <slot
                name="actionsButton"
                :selectedItems="selected"
                :refresher="updateTableContent"
                :pageLoader="pageLoad"
                :permissions="permissionChecker"
              ></slot>
            </div>
          </div>
          <!-- Export Sidebar start -->
          <ExportSidebar
            v-if="permissionChecker('export') && exportHeaders"
            ref="exportSidebar"
            :exportHeaders="exportHeaders"
            :downloadItem="downloadItem"
            :submitFilterValues="submitFilterValues"
            :resetFilterValues="resetFilterValues"
            :filters="filters"
            :setTableFiltersValues="setTableFiltersValues"
          />
          <!--        end::Export Sidebar-->

          <!-- Filter Sidebar start -->
          <FilterSidebar
            v-if="filters.length > 0"
            ref="filterSidebar"
            :submitFilterValues="submitFilterValues"
            :resetFilterValues="resetFilterValues"
            :filters="filters"
            :setTableFiltersValues="setTableFiltersValues"
          />
          <!--end::Filter Sidebar-->
        </template>
        <template
          v-if="!disableSelectAllCheckbox"
          v-slot:header.data-table-select="{ props: { value }, on: { input } }"
        >
          <div
            class="form-check form-check-sm form-check-custom form-check-solid me-5 min-w-20px min-h-20px"
          >
            <input
              class="form-check-input"
              type="checkbox"
              v-model="allSelected"
              @click="selectAll"
            />
          </div>
        </template>

        <template v-slot:item.data-table-select="{ item }">
          <div
            class="form-check form-check-sm form-check-custom form-check-solid me-5 min-w-20px min-h-20px"
          >
            <input
              class="form-check-input"
              type="checkbox"
              v-model="selected"
              @click="selectOne"
              :value="item"
            />
          </div>
        </template>
        <!-- column -->
        <template
          v-for="(head, i) in headers"
          v-slot:[`item.`+head.value]="{ item, header, value }"
        >
          <ComponentSelector
            v-bind:key="i"
            :item="item"
            :header="header"
            :value="value"
          ></ComponentSelector>
        </template>
        <template #item.action="{ item, header, value }">
          <component
            v-if="actionsComponent != null"
            v-bind:is="actionsComponent"
            :item="item"
            :actions="actions"
            :handle_function_call="handle_function_call"
          >
            <template #actionsDropdownContent>
              <slot
                name="rowActionsDropdown"
                :permissions="permissionChecker"
                :pageLoader="pageLoad"
                :item="item"
                :updateTable="updateTableContent"
              ></slot>
            </template>
          </component>
          <slot
            v-else
            name="rowActions"
            :permissions="permissionChecker"
            :pageLoader="pageLoad"
            :item="item"
            :updateTable="updateTableContent"
          ></slot>
        </template>
        <!-- column end -->
      </v-data-table>
    </div>
    <div v-if="items.length" class="v-datatable-pagination-row">
      <Pagination
        :setitemPerPage="setitemPerPage"
        :getitemPerPage="getitemPerPage"
        :getpageNumber="getpageNumber"
        :setpageNumber="setpageNumber"
        :totalItems="totalItems"
        :pageCount="pageCount"
        :updateTableContent="updateTableContent"
      ></Pagination>
    </div>
    <component
      v-if="permissionChecker('create')"
      v-bind:is="addComponent"
      :pageLoader="pageLoad"
      :refresher="updateTableContent"
      ref="addItem"
    ></component>
    <component
      v-if="permissionChecker('update')"
      v-bind:is="editComponent"
      :refresher="updateTableContent"
      :pageLoader="pageLoad"
      ref="editModal"
    ></component
    ><component
      v-if="uploadDocComponent"
      v-bind:is="uploadDocComponent"
      :refresher="updateTableContent"
      :pageLoader="pageLoad"
      ref="uploadDoc"
      :upload_type="uploadUrl"
    ></component>
    <component
      v-if="
        permissionChecker('create') && table_properties.title === 'Contracts'
      "
      v-bind:is="duplicateComponent"
      :refresher="updateTableContent"
      :pageLoader="pageLoad"
      ref="duplicateModal"
    ></component>
    <component
      v-if="permissionChecker('duplicate')"
      v-bind:is="duplicateComponent"
      :refresher="updateTableContent"
      :pageLoader="pageLoad"
      ref="duplicateModal"
    ></component>
    <component
      v-if="permissionChecker('download_return_plugin')"
      v-bind:is="downloadPluginComponent"
      :pageLoader="pageLoad"
      ref="downloadPluginModal"
    ></component>
    <component
      v-bind:is="showDetailComponent"
      :showUrl="urls?.showUrl"
      :detailLabel="detailLabel"
      :updateDatatable="updateTableContent"
      ref="showDetails"
    ></component>
    <component
      v-bind:is="addCommentComponent"
      ref="addCommentComponent"
    ></component>
    <component
      v-bind:is="manageHeaders"
      ref="manageHeadersComponent"
    ></component>
    <component
      v-bind:is="myAliceConfig"
      ref="myAliceConfigComponent"
    ></component>
    <div v-if="$slots.bottom">
      <slot name="bottom"></slot>
    </div>
  </div>
</template>

<script>
import ComponentSelector from "@/own/components/datatable/ComponentSelector.vue";
import DataTableActionSelector from "@/own/components/DataTableActionSelector.vue";
import DatatableDetail from "@/own/components/datatable/DatatableDetail";
import Pagination from "@/own/components/pagination/Pagination.vue";
import FilterSidebar from "@/own/components/sidebars/FilterSidebar.vue";
import ExportSidebar from "@/own/components/sidebars/ExportSidebar.vue";

import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";
import { SET_PAGE_LOADING } from "@/core/services/store/config.module";
import { SET_ITEM_FOR_ACTION } from "@/core/services/store/main.module";
import getPageWidthHeight from "@/own/mixins/getPageWidthHeight";
import ManageHeaders from "@/own/components/datahub/webhooks/ManageHeaders.vue";
import MyaliceConfig from "@/own/components/datahub/webhooks/MyaliceConfig.vue";

export default {
  name: "DataTable",
  mixins: [getPageWidthHeight],
  props: {
    id_field_name: {
      type: String,
      default: "id",
    },
    /** @type {{UPDATE_TABLE_STRUCTURE:string,UPDATE_TABLE_DATA:string,SET_TABLE_SORTBY:string,SET_TABLE_FILTER:string,SET_TABLE_PERPAGE:string,SET_TABLE_PAGE:string,EXPORT_TABLE_DATA:string}} */
    table_state: {
      type: Object,
      required: true,
    },
    /** @type {{getTableFilters:string,getTableState:string,getTableData:string,getTableHeaders:string,getTableProperties:string,getTableExportUrl:string,getTablePermissions:string}} */
    store_names: {
      type: Object,
      required: true,
    },
    addComponent: {},
    editComponent: {},
    duplicateComponent: {},
    uploadComponent: {},
    multiUploadComponent: {},
    generateComponent: {},
    generatePermission: { type: String, default: "print" },
    printComponent: {},
    downloadPluginComponent: {},
    uploadDocComponent: null,
    showDetailComponent: null,
    addCommentComponent: null,
    addButtonText: {},
    detailLabel: { type: String, default: null },
    delete_url: {
      type: String,
    },
    /** @type {{activate: string, delete: string, status: string}} */
    urls: {
      type: Object,
    },
    disableSelectAllCheckbox: {
      type: Boolean,
      default: false,
      required: false,
    },
    bulkRemoveOption: {
      type: Boolean,
      default: false,
      required: false,
    },
    isFilterRequiredForExport: {
      type: Boolean,
      default: false,
    },
    actionsComponent: {
      default: () => DataTableActionSelector,
    },
    overrideActionsWith: { type: Array, default: () => [] },
  },
  components: {
    DataTableActionSelector,
    DatatableDetail,
    FilterSidebar,
    ExportSidebar,
    Pagination,
    ComponentSelector,
  },
  data: () => ({
    toolBarVisionState: false,
    selected: [],
    allSelected: false,
    manageHeaders: ManageHeaders,
    myAliceConfig: MyaliceConfig,
  }),
  mounted() {
    this.onResize();
    window.addEventListener("resize", this.onResize, { passive: true });
  },
  computed: {
    uploadUrl: function () {
      let url = "";
      if (this.urls && this.urls.hasOwnProperty("uploadDoc"))
        url = this.urls.uploadDoc;
      return url;
    },
    isToolbarVisiable: function () {
      return this.$vuetify.breakpoint.smAndUp ? true : this.toolBarVisionState;
    },
    table_permissions: function () {
      /** @type {Array<string>} */
      let permissions = [];
      try {
        if (this.$store.getters[this.store_names.getTablePermissions]) {
          permissions = this.$store.getters[
            this.store_names.getTablePermissions
          ].map(function (perm) {
            return perm.name;
          });
        }
      } catch (error) {
        // this.$swal("Can not load table permissions!");
      }
      return permissions;
    },
    table_properties: function () {
      /** @type {{title:string, description: string}} */
      let properties = null;
      try {
        properties = this.$store.getters[this.store_names.getTableProperties];
      } catch (error) {
        // this.$swal("Can not load table properties!");
      }
      return properties;
    },
    orderBy: {
      get: function () {
        /** @type {string} */
        let sortBy = this.$store.getters[this.store_names.getTableState].sortBy;
        return sortBy;
      },
      set: function (newValue) {
        if (newValue) {
          this.$store.commit(this.table_state.SET_TABLE_SORTBY, newValue);
          this.$store.dispatch(
            this.table_state.UPDATE_TABLE_DATA,
            this.$store.getters[this.store_names.getTableState]
          );
        } else {
          this.$store.commit(this.table_state.SET_TABLE_SORTBY, "");
          this.$store.dispatch(
            this.table_state.UPDATE_TABLE_DATA,
            this.$store.getters[this.store_names.getTableState]
          );
        }
      },
    },
    sortOrder: {
      get: function () {
        let sortOrder = true;
        if (
          this.$store.getters[this.store_names.getTableState].sortOrder ===
          "asc"
        ) {
          sortOrder = false;
        }
        return sortOrder;
      },
      set: function (newValue) {
        if (newValue === true) {
          this.$store.commit(this.table_state.SET_TABLE_SORTORDER, "desc");
        } else {
          this.$store.commit(this.table_state.SET_TABLE_SORTORDER, "asc");
        }
        this.$store.dispatch(
          this.table_state.UPDATE_TABLE_DATA,
          this.$store.getters[this.store_names.getTableState]
        );
      },
    },
    headers: function () {
      /**
       * @type {Array<
          {
              value: string,
              text: string,
              type: string,
              sortable: string,
              exportable: string,
              visible: string,
              mobile_visible: string,
              align: string,
              itemClass: string,
              width: string,
              class: string,
              export_order: integer,
            }>
                }
        */
      let headers = [];
      try {
        if (this.$vuetify.breakpoint.smAndDown) {
          headers = this.$store.getters[
            this.store_names.getTableHeaders
          ].filter(
            (col) => col.visible === true && col.mobile_visible === true
          );
        } else {
          headers = this.$store.getters[
            this.store_names.getTableHeaders
          ].filter((col) => col.visible === true);
        }
        headers = headers.map((header) => {
          const mappedHeader = { ...header };
          mappedHeader.text = this.translateApiLabel(mappedHeader.text);
          mappedHeader.class =
            "text-muted fw-bolder font-size-sm text-uppercase poppins-ls";
          mappedHeader.itemClass = "second-text";
          mappedHeader.width = undefined;
          if (mappedHeader.value === "action") {
            mappedHeader.class =
              "pe-10 text-muted fw-bolder font-size-sm text-uppercase poppins-ls";
          }
          return mappedHeader;
        });
      } catch {
        // headers = this.$store.getters[this.store_names.getTableHeaders].filter(
        //   (col) => col.visible === true
        // );
      }

      return headers;
    },
    exportHeaders: function () {
      /** @type {Array<{order:string,text:string,value:string}>} */
      let exportHeaders = [];
      try {
        exportHeaders = this.$store.getters[
          this.store_names.getTablePermissions
        ]
          .find((col) => col.name === "export")
          ?.columns.sort((a, b) => a.order - b.order);
      } catch (error) {
        exportHeaders = [];
      }
      return exportHeaders.map((header) => ({
        ...header,
        text: this.translateApiLabel(header.text),
      }));
    },
    items: function () {
      /**
       * @type {Array<{}>}
       * @example [{"id":891,"email":"rafi.ogchy@gmail.com","extra_data":null}]
       */
      let items = [];
      try {
        items = this.$store.getters[this.store_names.getTableData].data;
      } catch {
        items = null;
      }
      return items;
    },
    //-------------------activities------------------------
    actions: function () {
      if (this.overrideActionsWith?.length) {
        return [...this.overrideActionsWith];
      }
      return [
        {
          title: this.translateApiLabel("View"),
          type: "normal",
          method: "activateView",
          isVisible: this.permissionChecker("show"),
        },
        {
          title: this.translateApiLabel("Reset"),
          type: "normal",
          method: "resetItem",
          isVisible: this.permissionChecker("reset"),
        },
        {
          title: this.translateApiLabel("Reset Password"),
          type: "normal",
          method: "activateAction",
          isVisible: this.permissionChecker("activate"),
        },
        {
          title: this.translateApiLabel("Refresh token"),
          type: "normal",
          method: "generateAction",
          isVisible: this.permissionChecker("generate"),
        },
        {
          title: this.translateApiLabel("Status"),
          type: "status",
          method: "statusAction",
          isVisible: this.permissionChecker("status"),
        },
        {
          title: this.translateApiLabel("Edit"),
          type: "normal",
          method: "editItem",
          isVisible: this.permissionChecker("update"),
        },
        {
          title: this.translateApiLabel("Remove"),
          type: "normal",
          method: "removeItem",
          isVisible: this.permissionChecker("destroy"),
        },
        {
          title: this.translateApiLabel("Export"),
          type: "normal",
          method: "exportChecklist",
          isVisible: this.permissionChecker("export_checklists"),
        },
        {
          title: this.translateApiLabel("Duplicate"),
          type: "normal",
          method: "duplicateAction",
          isVisible: this.permissionChecker("duplicate"),
        },
        {
          title: this.translateApiLabel("Duplicate"),
          type: "normal",
          method: "duplicateAction",
          isVisible:
            this.permissionChecker("create") &&
            this.table_properties.title === "Contracts",
        },
        {
          title: this.translateApiLabel("Duplicate"),
          type: "normal",
          method: "duplicateFinAction",
          isVisible:
            this.permissionChecker("create") &&
            this.table_properties.title === "Services",
          // isVisible: this.permissionChecker("duplicateForFin"),
        },
        {
          title: this.translateApiLabel("Duplicate"),
          type: "normal",
          method: "duplicateItem",
          isVisible: this.permissionChecker("duplicate_oms_rule"),
        },
        {
          title: this.translateApiLabel("Export SKU"),
          type: "normal",
          method: "exportSkuAction",
          isVisible:
            this.permissionChecker("export") &&
            this.table_properties.title === "Bundles",
        },
        {
          title: this.translateApiLabel("Install Plugin"),
          type: "normal",
          method: "downloadPlugin",
          isVisible: this.permissionChecker("download_return_plugin"),
        },
        {
          title: this.translateApiLabel("Upload doc"),
          method: "showUploadDocModal",
          isVisible:
            Boolean(this.uploadDocComponent) &&
            this.permissionChecker("upload_doc"),
        },
        {
          title: this.translateApiLabel("Generate client token"),
          type: "normal",
          method: "generateClientToken",
          isVisible: this.permissionChecker("generate_webhook_client_secret"),
        },
        {
          title: this.translateApiLabel("Retry with current data"),
          type: "normal",
          method: "generateClientToken",
          isVisible: this.permissionChecker("retry_with_current_data"),
        },
        {
          title: this.translateApiLabel("History"),
          type: "normal",
          method: "updateSimplifiedRules",
          isVisible: this.permissionChecker("oms_rule_history"),
        },
      ];
    },
    //-------------------pagination----------------------
    getitemPerPage: function () {
      /** @type {number} */
      let itemPerPage =
        this.$store.getters[this.store_names.getTableState].per_page;
      return itemPerPage;
    },
    getpageNumber: function () {
      /** @type {number} */
      let pageNumber = this.$store.getters[this.store_names.getTableState].page;
      return pageNumber;
    },
    totalItems: function () {
      /** @type {number} */
      let totalItems = this.$store.getters[this.store_names.getTableData].total;
      return totalItems;
    },
    pageCount: function () {
      /** @type {number} */
      let pageCount =
        this.$store.getters[this.store_names.getTableData].last_page;
      return pageCount;
    },
    itemPerPage: function () {
      /** @type {number} */
      let itemPerPage =
        this.$store.getters[this.store_names.getTableState].per_page;
      return itemPerPage;
    },
    //-------------------filter----------------------
    filters: function () {
      /** @type {Array<{name: string, title: string, type: string, related_columns: string[]}>} */
      let filters = [];
      try {
        filters = this.$store.getters[this.store_names.getTableFilters];
      } catch {
        filters = [];
      }
      return filters;
    },
    // filters: function () {
    //   /** @type {Array<{name: string, title: string, type: string, related_columns: string[]}>} */
    //   let filters = [];
    //   try {
    //     filters = this.$store.getters[this.store_names.getTableFilters];
    //   } catch {
    //     filters = [];
    //   }
    //   return filters.map((filter) => {
    //     const mappedFilter = { ...filter };
    //     if (mappedFilter?.title) {
    //       mappedFilter.title = this.translateApiLabel(mappedFilter.title);
    //     }
    //     if (mappedFilter?.label) {
    //       mappedFilter.label = this.translateApiLabel(mappedFilter.label);
    //     }
    //     if (Array.isArray(mappedFilter?.values)) {
    //       mappedFilter.values = mappedFilter.values.map((option) => {
    //         if (typeof option === "string") {
    //           return this.translateApiLabel(option);
    //         }
    //         if (option && typeof option === "object") {
    //           const mappedOption = { ...option };
    //           if (mappedOption.text) {
    //             mappedOption.text = this.translateApiLabel(mappedOption.text);
    //           }
    //           if (mappedOption.title) {
    //             mappedOption.title = this.translateApiLabel(mappedOption.title);
    //           }
    //           return mappedOption;
    //         }
    //         return option;
    //       });
    //     }
    //     return mappedFilter;
    //   });
    // },
  },
  methods: {
    normalizeApiLabel(label) {
      return String(label || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    },
    getApiLabelTranslationKey(label) {
      const explicitMap = {
        tools: "tools",
        view: "view",
        reset: "reset",
        reset_password: "reset_password",
        refresh_token: "refresh_token",
        status: "status",
        edit: "edit",
        remove: "remove",
        export: "export",
        duplicate: "duplicate",
        export_sku: "export_sku",
        install_plugin: "install_plugin",
        upload_doc: "upload_doc",
        generate_client_token: "generate_client_token",
        retry_with_current_data: "retry_with_current_data",
        history: "history",
        system_code: "system_code",
        sku_type: "sku_type",
        sku_label: "sku_label",
        hs_code: "hs_code",
        country_of_origin: "country_of_origin",
        uom_type: "uom_type",
        cost_price: "cost_price",
        landed_cost: "landed_cost",
        dg_type: "dg_type",
        count_in_box: "count_in_box",
        out_of_stock_notification_threshold:
          "out_of_stock_notification_threshold",
        storage_type: "storage_type",
        dangerous_goods_note: "dangerous_goods_note",
        expiry_control: "expiry_control",
        serial_number_control: "serial_number_control",
        temperature_control: "temperature_control",
        preorder_enable: "preorder_enable",
        force_split: "force_split",
        alternative_skus: "alternative_skus",
        new_item: "new_item",
        enabled: "enabled",
        disabled: "disabled",
        barcode: "barcode",
        qr: "qr",
      };
      const normalizedLabel = this.normalizeApiLabel(label);
      return explicitMap[normalizedLabel] || normalizedLabel;
    },
    translateApiLabel(label) {
      if (!label || typeof label !== "string") return label;
      const key = this.getApiLabelTranslationKey(label);
      if (this.$te(key)) {
        return this.$t(key);
      }
      return label;
    },
    // connector for action functions
    editActionFunction(props) {
      this.editItem(props[0]);
    },
    pendingActionFunction(props) {
      this.statusActionNew(props[0], props[1]);
    },
    verifiedActionFunction(props) {
      this.statusActionNew(props[0], props[1]);
    },
    disabledActionFunction(props) {
      this.statusActionNew(props[0], props[1]);
    },
    openActionFunction(props) {
      this.statusActionNew(props[0], props[1]);
    },
    resolvedActionFunction(props) {
      this.statusActionNew(props[0], props[1]);
    },
    unresolvedActionFunction(props) {
      this.statusActionNew(props[0], props[1]);
    },
    removeActionFunction(props) {
      this.removeItem(props[0]);
    },
    destroyActionFunction(props) {
      this.removeItem(props[0]);
    },
    add_commentActionFunction(props) {
      this.$refs.addCommentComponent.setId(props[0].id);
      this.$refs.addCommentComponent.toggleModal();
    },
    upload_docActionFunction(props) {
      this.$store.commit(SET_ITEM_FOR_ACTION, props[0]);
      this.pageLoad(true);
      this.$refs.uploadDoc.toggleModal();
    },
    headersActionFunction(props) {
      this.$store.commit(SET_ITEM_FOR_ACTION, props[0]);
      this.$refs.manageHeadersComponent.toggleModal();
    },
    my_alice_configActionFunction(props) {
      this.$store.commit(SET_ITEM_FOR_ACTION, props[0]);
      this.$refs.myAliceConfigComponent.toggleModal();
    },
    test_modeActionFunction(props) {
      this.statusActionNew(props[0], props[1]);
    },
    retry_with_current_dataActionFunction(props) {
      this.pageLoad(true);
      let data = { id: props[0].id };
      ApiService.post("/datahub/webhooks-logs/retry_with_current_data", data)
        .then(() => {
          Swal.fire(
            this.$t("notificationtitle_success"),
            this.$t("retry_successful"),
            "success"
          );
          this.updateTableContent();
        })
        .catch(() => {
          this.pageLoad(false);
        });
    },
    statusActionNew(item, type) {
      this.pageLoad(true);
      let data = { id: item.id, status: type };
      ApiService.post(this.urls.status, data)
        .then(() => {
          Swal.fire(
            this.$t("activation"),
            this.$t("status_updated"),
            "success"
          );
          this.updateTableContent();
        })
        .catch(() => {
          this.pageLoad(false);
        });
    },

    // end of connectors
    /**
     * used to call functions by name
     * @param {string} function_name - name of function
     * @param {string} prop - property for function
     */
    handle_function_call(function_name, prop) {
      // this[`${function_name}`](prop);
      if (this.overrideActionsWith.length) {
        this.$emit(function_name, prop);
      } else {
        this[`${function_name}`](prop);
      }
    },
    handleExportSidebarClick() {
      this.$refs.exportSidebar.$data.exportSidebar = true;
    },
    handleFilterSidebarClick() {
      this.$refs.filterSidebar.$data.filterSidebar = true;
    },
    handleDoubleClick(val, { item }) {
      this.$refs.showDetails.setId(item.id);
      this.$refs.showDetails.toggleModal();
    },
    // loadDetailData(id) {
    //   this.$store.commit(SET_PAGE_LOADING, true);
    //   let data = { id: id };
    //   ApiService.post(this.urls.detail, data)
    //     .then((response) => {
    //       this.$store.commit(SET_PAGE_LOADING, false);
    //       this.$refs.showDetails.setData(response.data.data);
    //       this.$refs.showDetails.toggleModal();
    //     })
    //     .catch(() => {
    //       this.$store.commit(SET_PAGE_LOADING, false);
    //     });
    // },
    deleteAllItems() {
      this.selected.splice(0, this.selected.length);
      this.allSelected = false;
    },
    selectOne(event) {
      this.allSelected && this.deleteItem(event.target.value);

      this.allSelected = false;
    },
    selectAll(event) {
      this.deleteAllItems();
      if (event.target.checked) {
        this.items.forEach((item) => {
          this.selected.push(item);
        });
      }
    },
    //\\
    onResize() {
      if (this.$vuetify.breakpoint.smAndDown) {
        this.toolBarVisionState = false;
      } else if (this.$vuetify.breakpoint.mdAndUp) {
        this.toolBarVisionState = true;
      }
    },
    /**
     * Check is there permission included
     * @param {string} permType - permission type
     * @returns {boolean}
     */
    permissionChecker(permType) {
      return this.table_permissions.includes(permType);
    },

    /**
     * set app loader status
     * @param {boolean} state - state to set
     */
    pageLoad(state) {
      this.$store.commit(SET_PAGE_LOADING, state);
    },
    async updateTableContent() {
      this.pageLoad(true);
      await this.$store
        .dispatch(
          this.table_state.UPDATE_TABLE_DATA,
          this.$store.getters[this.store_names.getTableState]
        )
        .then(() => {
          this.selected = [];
        });
      this.$forceUpdate();
      this.pageLoad(false);
    },

    showAddItemModal() {
      this.$store.commit(SET_ITEM_FOR_ACTION, null);
      this.$refs.addItem.toggleModal();
    },
    showUploadModal(type) {
      this.$router.push({
        name: "settings.uploads",
        query: { type: type },
      });
    },
    showGenerateModal() {
      this.$refs.generate.toggleModal();
    },
    applyReprocessAction() {
      if (this.selected.length > 0) {
        this.pageLoad(true);
        const data = { id: this.selected.map((row) => row.id) };
        ApiService.post(this.urls.reprocess, data)
          .then(() => {
            Swal.fire(
              this.$t("reprocess"),
              this.$t("data_reprocessed"),
              "success"
            );
            this.selected = [];
            this.updateTableContent();
          })
          .finally(() => {
            this.pageLoad(false);
          });
      } else {
        Swal.fire({
          title: this.$t("warning"),
          text: this.$t("please_select_at_least_one_record"),
          icon: "warning",
          showConfirmButton: false,
          timer: 2500,
        });
      }
    },
    duplicateFinAction(item) {
      this.pageLoad(true);
      const data = { id: item.id, name: item.name };
      ApiService.post(this.urls.duplicate, data)
        .then(() => {
          this.pageLoad(false);
          Swal.fire(this.$t("success"), this.$t("data_duplicated"), "success");
          this.selected = [];
          this.updateTableContent();
        })
        .catch(() => {
          this.pageLoad(false);
        });
    },
    //----------------------activities-------------------------
    activateView(item) {
      let routeData = this.$router.resolve({
        name: "invoiceTemp",
        query: { id: item.id },
      });
      window.open(routeData.href, "_blank");
    },
    resetItem(item) {
      const data = { id: item.id };
      ApiService.post(this.urls.reset, data)
        .then(() => {
          Swal.fire(
            this.$t("reset"),
            this.$t("record_has_been_reset"),
            "success"
          );
          this.updateTableContent();
        })
        .catch(() => {
          this.pageLoad(false);
        });
    },
    editItem(item) {
      this.$store.commit(SET_ITEM_FOR_ACTION, item);
      this.$refs.editModal.toggleModal();
    },
    exportChecklist(item) {
      this.$refs.editModal.exportChecklist(item);
    },
    removeItem(item) {
      Swal.fire({
        title: this.$t("notificationtitle_are_you_sure"),
        text: this.$t("notificationmessage_you_wont_be_able_to_revert_this"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: this.$t("cancel"),
        confirmButtonText: this.$t("notificationmessage_yes_delete_it"),
      }).then((result) => {
        if (result.isConfirmed) {
          this.pageLoad(true);
          //TODO: change data sending to id
          const data = { id: item.id };
          ApiService.post(this.delete_url, data)
            .then(() => {
              Swal.fire(
                this.$t("notificationtitle_deleted"),
                this.$t("record_has_been_deleted"),
                "success"
              );
              this.updateTableContent();
            })
            .catch(() => {
              this.pageLoad(false);
            });
        }
      });
    },
    generateAction(item) {
      this.pageLoad(true);
      let data = { id: item.id };
      ApiService.post(this.urls.generate, data)
        .then((res) => {
          this.$emit("handleTokenModal", res.data.token);
          this.updateTableContent();
        })
        .catch(() => {
          this.pageLoad(false);
        });
    },
    generateClientToken(item) {
      this.pageLoad(true);
      let data = { id: item.id };
      ApiService.post(this.urls.generateClientToken, data)
        .then((res) => {
          this.$emit("handleTokenModal", res.data.webhook_client_secret);
          this.updateTableContent();
        })
        .catch(() => {
          this.pageLoad(false);
        });
    },
    activateAction(item) {
      this.pageLoad(true);
      let data = { id: item.id };
      ApiService.post(this.urls.activate, data)
        .then(() => {
          Swal.fire(
            this.$t("activation"),
            this.$t("activation_succeeded_check_email"),
            "success"
          );
          this.updateTableContent();
        })
        .catch(() => {
          this.pageLoad(false);
        });
    },
    exportSkuAction(item) {
      this.$store.commit(SET_PAGE_LOADING, true);
      // console.log("order table state", this.$store.getters.getORDERSTableState);
      ApiService.post("/fulfillment/bundles/export", {
        id: item.id,
        export_type: "skus",
        columns: [
          ...this.$store.getters[this.store_names.getExportSkuColumns].map(
            (column) => column.value
          ),
        ],
      }).then((res) => {
        const link = document.createElement("a");
        link.href = res.data.url;
        link.download = "Export.csv";
        link.click();
        setTimeout(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
        }, 1500);
        link.remove();
      });
    },
    duplicateAction(item) {
      this.$store.commit(SET_ITEM_FOR_ACTION, item);
      this.$refs.duplicateModal.toggleModal();
    },
    downloadPlugin(item) {
      this.$store.commit(SET_ITEM_FOR_ACTION, item);
      this.$refs.downloadPluginModal.toggleModal();
    },
    showUploadDocModal(item) {
      this.$store.commit(SET_ITEM_FOR_ACTION, item);
      this.pageLoad(true);
      this.$refs.uploadDoc.toggleModal();
    },
    generateToken(item) {
      this.$store.commit(SET_PAGE_LOADING, true);
      ApiService.post(this.urls.generateToken, {
        customer_system_code: item.systemCode,
      }).then(({ data }) => {
        Swal.fire({
          title: this.$t("token_generated"),
          text: `${data.token}`,
          icon: "success",
          showConfirmButton: true,
          // timer: 2500,
        });
        this.$store.commit(SET_PAGE_LOADING, false);
      });
    },
    generateRefreshToken(item) {
      this.$store.commit(SET_PAGE_LOADING, true);
      ApiService.post(this.urls.generateRefreshToken, {
        customer_system_code: item.systemCode,
      }).then(({ data }) => {
        Swal.fire({
          title: this.$t("refresh_token_generated"),
          text: `${data.token}`,
          icon: "success",
          showConfirmButton: true,
          // timer: 2500,
        });
        this.$store.commit(SET_PAGE_LOADING, false);
      });
    },
    statusAction(item) {
      if (item.status_label === "Cancelled") return;
      this.pageLoad(true);
      let status = null;
      if (item.status_label === "Disabled" || item.status_label === "Draft") {
        status = "Enabled";
      } else status = "Disabled";
      let data = { id: item.id, status };
      ApiService.post(this.urls.status, data)
        .then(() => {
          Swal.fire(
            this.$t("activation"),
            this.$t("status_updated"),
            "success"
          );
          this.updateTableContent();
        })
        .catch(() => {
          this.pageLoad(false);
        });
    },

    //------------------pagination-------------------
    setitemPerPage(val) {
      this.$store.commit(this.table_state.SET_TABLE_PERPAGE, val);
      this.deleteAllItems();
    },
    setpageNumber(val) {
      this.$store.commit(this.table_state.SET_TABLE_PAGE, val);
      this.deleteAllItems();
    },
    //---------------------export-------------------------
    isAnyFilterSelected() {
      return this.$store.getters[this.store_names.getTableState].filters
        .length > 0
        ? true
        : false;
    },
    downloadItem(columns) {
      if (this.isFilterRequiredForExport && !this.isAnyFilterSelected()) {
        this.$refs.exportSidebar.$data.exportSidebar = false;
        Swal.fire({
          title: this.$t("warning"),
          text: this.$t(
            "notificationmessage_please_select_at_least_one_filter"
          ),
          icon: "warning",
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }
      this.$store.commit(SET_PAGE_LOADING, true);
      const payload = {
        columns: columns,
        filters: this.$store.getters[this.store_names.getTableState].filters,
      };

      if (this.selected.length) {
        payload.export_ids = this.selected.map((item) => item.id);
      }

      this.$store
        .dispatch(this.table_state.EXPORT_TABLE_DATA, payload)
        .then(() => {
          const link = document.createElement("a");
          link.href = this.$store.getters[this.store_names.getTableExportUrl];
          link.download = "ProblemWithExport.csv";
          link.click();
          setTimeout(() => {
            this.$store.commit(SET_PAGE_LOADING, false);
          }, 1500);
          // this.$refs.exportSidebar.$refs.export.toggleModal();
          this.$refs.exportSidebar.$data.exportSidebar = false;
          link.remove();
        });
    },

    //--------------------filter---------------------
    setTableFiltersValues(val) {
      this.$store.commit(this.table_state.SET_TABLE_FILTER, val);
    },
    submitFilterValues() {
      this.$store.commit(SET_PAGE_LOADING, true);
      let state = { ...this.$store.getters[this.store_names.getTableState] };
      state.page = 1;
      this.$store
        .dispatch(this.table_state.UPDATE_TABLE_DATA, state)
        .then(() => {
          this.$store.commit(this.table_state.SET_TABLE_PAGE, 1);

          this.$nextTick(() => {
            this.$store.commit(SET_PAGE_LOADING, false);
            this.selected = [];
          });
        })
        .catch(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
        });
    },
    resetFilterValues() {
      this.$store.commit(SET_PAGE_LOADING, true);
      this.$store.commit(this.table_state.SET_TABLE_FILTER, []);
      this.$store
        .dispatch(
          this.table_state.UPDATE_TABLE_DATA,
          this.$store.getters[this.store_names.getTableState]
        )
        .then(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
          this.selected = [];
        });
    },
    resetSelectedItems() {
      if (this.selected.length === 0) {
        return;
      }
      let idArr = [];
      this.selected.map((item) => {
        idArr.push(item.id);
      });
      Swal.fire({
        title: this.$t("notificationtitle_are_you_sure"),
        text: this.$t("you_will_reset_all_selected_items"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: this.$t("cancel"),
        confirmButtonText: this.$t("yes_reset_them"),
      }).then((result) => {
        if (result.isConfirmed) {
          this.pageLoad(true);
          //TODO: change data sending to id
          const data = { id: idArr };
          ApiService.post(this.urls.reset, data)
            .then(() => {
              Swal.fire(
                this.$t("reset"),
                this.$t("records_have_been_reset"),
                "success"
              );
              this.selected = [];
              this.updateTableContent();
            })
            .catch(() => {
              this.selected = [];
              this.pageLoad(false);
            });
        }
      });
    },
    duplicateItem(item) {
      Swal.fire({
        title: this.$t("notificationtitle_are_you_sure"),
        text: this.$t("duplicate_order_strategy_question"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: this.$t("cancel"),
        confirmButtonText: this.$t("yes_duplicate_it"),
      }).then((result) => {
        if (result.isConfirmed) {
          this.pageLoad(true);
          //TODO: change data sending to id
          const data = { id: item.id };
          ApiService.post(this.urls?.duplicate, data)
            .then(() => {
              Swal.fire(
                this.$t("notificationtitle_created"),
                this.$t("record_has_been_updated"),
                "success"
              );
              this.updateTableContent();
            })
            .catch(() => {
              this.pageLoad(false);
            });
        }
      });
    },
    updateSimplifiedRules(item) {
      this.$store.commit(SET_ITEM_FOR_ACTION, item);
      this.$emit("updateSimplifiedRules");
    },
  },
  beforeDestroy() {
    this.$store.commit(this.table_state.SET_TABLE_FILTER, []);
    if (typeof window === "undefined") return;

    window.removeEventListener("resize", this.onResize, { passive: true });
  },
};
</script>

<style scoped>
.q-status-menu {
  max-height: 350px;
  overflow-y: scroll;
}
.drop-item {
  font-weight: 500;
  color: rgb(98, 98, 98);
}
.drop-item:hover {
  font-weight: 600;
  background-color: #eee5ff;
  color: #8950fcff !important;
}
.drop-text {
  font-size: 9px;
}
</style>
<style lang="scss">
.row.justify-center:has(> .v-dialog__container:first-child) {
  position: absolute;
  height: 0;
  margin: 0;
}
</style>
